import React from "react"

import type { Message } from "@/types/Transcript"

interface TimestampDisplayProps {
  message: Message
  className?: string
}

/** `m:ss`, the one clock format the transcript states a time in. */
const formatSecondsToMinutes = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

const formatInterval = (startSeconds: number, endSeconds: number): string => {
  const start = formatSecondsToMinutes(startSeconds)
  if (startSeconds === endSeconds) {
    return start
  }
  const end = formatSecondsToMinutes(endSeconds)
  return `${start} — ${end}`
}

/**
 * When a message runs, in seconds from the start of the conversation, from
 * whichever of the three stored timing shapes the message carries. A transcript
 * with no timing at all — a text conversation — has none, and so does a message
 * that records only one end of its interval.
 */
const getMessageInterval = (
  message: Message,
): { start: number; end: number } | null => {
  // beginning/end (legacy format)
  if (message.beginning != null && message.end != null) {
    return { start: message.beginning, end: message.end }
  }

  // start_offset/end_offset (milliseconds)
  if (message.start_offset != null && message.end_offset != null) {
    return {
      start: message.start_offset / 1000,
      end: message.end_offset / 1000,
    }
  }

  // start_timestamp/end_timestamp (string format)
  if (message.start_timestamp != null && message.end_timestamp != null) {
    const startSeconds = parseFloat(message.start_timestamp)
    const endSeconds = parseFloat(message.end_timestamp)
    if (!isNaN(startSeconds) && !isNaN(endSeconds)) {
      return { start: startSeconds, end: endSeconds }
    }
  }

  return null
}

/**
 * A message's start time as the transcript writes it, for a caller that points
 * at the message from elsewhere and needs the same label the reader will find.
 * A message the transcript states no time for has none, so the caller never
 * names a time the reader cannot then see on the bubble.
 */
export const getMessageStartLabel = (message: Message): string | null => {
  const interval = getMessageInterval(message)
  return interval == null ? null : formatSecondsToMinutes(interval.start)
}

const getTimestampData = (message: Message): string | null => {
  const interval = getMessageInterval(message)
  return interval == null ? null : formatInterval(interval.start, interval.end)
}

export const TimestampDisplay: React.FC<TimestampDisplayProps> = ({
  message,
  className = "",
}) => {
  const timestamp = getTimestampData(message)

  if (!timestamp) {
    return null
  }

  return (
    <div className={`text-xs mt-1.5 opacity-60 font-normal ${className}`}>
      {timestamp}
    </div>
  )
}
